import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import {shouldUpdate} from 'recompose'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ObjUtil} from 'yougile-util'
import {Hub, ReportColumn} from 'yougile-model'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {Diagram} from '@components/timeline-new/diagram'
import {Tasks} from '@components/timeline-new/tasks'
import {TimelineControls} from '@components/timeline-new/timeline-controls'
import {
  TimelineNewContext,
  TimelineNewStore,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {useSyncEvent} from '@containers/hooks'
import {Sync} from '../../../client/sync'
import {withUpdaters} from '../../../containers/updaters'
import {SyncUpdater} from '../../../containers/updaters/sync-updater'
import {getTasks} from './stats-reports-get-tasks'

const TwWrapper = tw.div`
  bg-primary
  flex
  flex-col
  select-none
  relative
  h-full
`

const TwMain = tw.div`
  flex-1
  flex
  min-h-0
  absolute
  w-full
  h-full
`

const TwTasks = tw(Tasks)`
  h-full
`

const TwDiagram = tw(Diagram)`
  flex-1
  h-full
`

const TwTitleRow = tw.div`
  flex
  items-center
  justify-between
  mx-16
  mb-4
  mt-[-6px]
`

const TwTitle = tw.div`
  h2-semibold
  tracking-tight
`

export interface TimelineNewProps {
  tasks: Hub[]
}

export const TimelineNew = observer((props: TimelineNewProps) => {
  const {tasks} = props
  const [, store] = useTimelineNewStore()

  useSyncEvent('current', {filter: '*'}, store.syncIfRequired, [store])

  useEffect(() => {
    store.setForceRootNodes(tasks)
  }, [tasks])

  return (
    <TwWrapper>
      <TwMain>
        <TwTasks />
        <TwDiagram />
      </TwMain>
    </TwWrapper>
  )
})

interface Props {
  column: ReportColumn
  title: string
}

export const StatsGant = shouldUpdate((prev, next) => {
  return !ObjUtil.deepEqual(prev, next, true)
})(
  withUpdaters(
    SyncUpdater({companyType: 'current', filter: '*', debounceTime: 200})
  )((props: Props) => {
    const {column, title} = props
    const box = Sync.getCurrentSandbox()
    const taskData = getTasks(box, column).map(x => box.getHub(x.id))

    const [timelineStore] = React.useState(
      () =>
        new TimelineNewStore(boardSettingsStore, {
          static: true
        })
    )
    useEffect(timelineStore.init, [timelineStore])

    return (
      <TimelineNewContext.Provider value={timelineStore}>
        <TwTitleRow>
          <TwTitle>{title || Lang.msg.component.filters.withoutName}</TwTitle>
          <TimelineControls className="!mr-0 bg-transparent" />
        </TwTitleRow>
        <TimelineNew tasks={taskData} />
      </TimelineNewContext.Provider>
    )
  })
)
